import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, PageLoader, PageContent } from '../../components';
import { useProfileSettings } from './hooks/useProfileSettings';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';
import { GeneralSettingsTab } from './components/GeneralSettingsTab';
import { AccountInformationTab } from './components/AccountInformationTab';
import { NotificationsTab } from './components/NotificationsTab';

export const ProfileSettings: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'general' | 'account' | 'notifications'>('general');
  const {
    profile,
    notifications,
    isLoading,
    isSaving,
    error,
    updateProfile,
    updateNotifications,
    saveProfile,
    saveNotifications,
    changePassword,
    uploadAvatar,
  } = useProfileSettings();

  const showLoader = useMinimumLoader(isLoading, { minimumDuration: 500 });

  const handleSaveAll = async () => {
    if (activeTab === 'notifications') {
      await saveNotifications();
    } else {
      await saveProfile();
    }
  };

  const tabs = [
    { id: 'general', label: t('profile.generalSettings'), icon: 'settings' },
    { id: 'account', label: t('profile.accountInformation'), icon: 'person' },
    { id: 'notifications', label: t('profile.notifications'), icon: 'notifications' },
  ] as const;

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  return (
    <PageContent>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('profile.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{t('profile.subtitle')}</p>
        </div>

        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="material-icons text-lg">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="min-h-[400px]">
          {activeTab === 'general' && (
            <GeneralSettingsTab
              profile={profile}
              onUpdateProfile={updateProfile}
              onChangePassword={changePassword}
              isSaving={isSaving}
            />
          )}
          {activeTab === 'account' && (
            <AccountInformationTab
              profile={profile}
              onUpdateProfile={updateProfile}
              onUploadAvatar={uploadAvatar}
              isSaving={isSaving}
            />
          )}
          {activeTab === 'notifications' && (
            <NotificationsTab
              notifications={notifications}
              onUpdateNotifications={updateNotifications}
            />
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <span className="material-icons text-red-500 mr-2">error</span>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Save Button - Hidden for General Settings tab */}
        {activeTab !== 'general' && (
          <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={handleSaveAll}
              disabled={isSaving}
              variant="gradient-multi"
              className="px-8 py-3"
            >
              {isSaving ? (
                <>
                  <span className="material-icons animate-spin mr-2">refresh</span>
                  {t('profile.saving')}
                </>
              ) : (
                <>
                  <span className="material-icons mr-2">save</span>
                  {t('profile.saveAllChanges')}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </PageContent>
  );
};
